import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackEvent } from '../hooks/useAnalytics';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('valmera-cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Alias for compatibility
  const cart = cartItems;

  useEffect(() => {
    localStorage.setItem('valmera-cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const addToCart = (product, selectedColor = null, quantity = 1) => {
    setCartItems((prev) => {
      // Create a unique key based on product id, size, and color
      const itemKey = `${product.id}-${product.selectedSize || '65x65'}-${selectedColor || ''}`;
      const existingItem = prev.find(
        (item) => `${item.id}-${item.selectedSize || '65x65'}-${item.selectedColor || ''}` === itemKey
      );

      if (existingItem) {
        return prev.map((item) =>
          `${item.id}-${item.selectedSize || '65x65'}-${item.selectedColor || ''}` === itemKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }

      return [...prev, { ...product, selectedColor, quantity }];
    });
    
    // Track add to cart event
    trackEvent({
      event_type: 'add_to_cart',
      product_id: product.id,
      metadata: { 
        size: product.selectedSize || '65x65',
        color: selectedColor,
        quantity: quantity,
        price: product.price
      }
    });
  };

  const removeFromCart = (productId, selectedSize = null, selectedColor = null) => {
    setCartItems((prev) =>
      prev.filter(
        (item) => !(`${item.id}-${item.selectedSize || '65x65'}-${item.selectedColor || ''}` === `${productId}-${selectedSize || '65x65'}-${selectedColor || ''}`)
      )
    );
    
    // Track remove from cart event
    trackEvent({
      event_type: 'remove_from_cart',
      product_id: productId,
      metadata: { size: selectedSize, color: selectedColor }
    });
  };

  const updateQuantity = (productId, selectedSize, selectedColor, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, selectedSize, selectedColor);
      return;
    }

    const itemKey = `${productId}-${selectedSize || '65x65'}-${selectedColor || ''}`;
    setCartItems((prev) =>
      prev.map((item) =>
        `${item.id}-${item.selectedSize || '65x65'}-${item.selectedColor || ''}` === itemKey
          ? { ...item, quantity }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  const value = {
    cart,
    cartItems,
    isCartOpen,
    setIsCartOpen,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
